import React, { useState, useEffect } from 'react';
import { ui } from '../../../config/uiSizes';

interface DeleteInvalidDataParametersProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
}

export const DeleteInvalidDataParameters: React.FC<DeleteInvalidDataParametersProps> = ({
  parameters,
  onParametersChange
}) => {
  const [removeNullValues, setRemoveNullValues] = useState<boolean>(
    parameters?.['removeNullValues'] !== undefined
      ? (parameters['removeNullValues'] as boolean)
      : true
  );
  const [removeEmptyStrings, setRemoveEmptyStrings] = useState<boolean>(
    parameters?.['removeEmptyStrings'] !== undefined
      ? (parameters['removeEmptyStrings'] as boolean)
      : true
  );
  const [removeEmptyArrays, setRemoveEmptyArrays] = useState<boolean>(
    parameters?.['removeEmptyArrays'] !== undefined
      ? (parameters['removeEmptyArrays'] as boolean)
      : false
  );
  const [removeEmptyObjects, setRemoveEmptyObjects] = useState<boolean>(
    parameters?.['removeEmptyObjects'] !== undefined
      ? (parameters['removeEmptyObjects'] as boolean)
      : false
  );
  const [invalidProperties, setInvalidProperties] = useState<string>(() => {
    const props = parameters?.['removeInvalidProperties'];
    if (Array.isArray(props)) {
      return props.join(', ');
    }
    return '';
  });

  useEffect(() => {
    if (onParametersChange) {
      const params: Record<string, unknown> = {
        removeNullValues,
        removeEmptyStrings,
        removeEmptyArrays,
        removeEmptyObjects
      };

      // Parse invalid properties string to array
      const invalidPropsArray = invalidProperties
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      if (invalidPropsArray.length > 0) {
        params['removeInvalidProperties'] = invalidPropsArray;
      }

      onParametersChange(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    removeNullValues,
    removeEmptyStrings,
    removeEmptyArrays,
    removeEmptyObjects,
    invalidProperties
  ]);

  return (
    <div className={`border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${ui.page.cardPadding}`}>
      <div className={`${ui.mb.title}`}>
        <h3 className={`${ui.text.sectionTitle} font-bold text-gray-900 dark:text-gray-100`}>Delete Invalid Data</h3>
        <p className={`${ui.text.label} text-gray-600 dark:text-gray-400 mt-1`}>
          Remove invalid data and clean up JSON files before migration.
        </p>
      </div>

      <div className="space-y-4">
        {/* Cleanup Options */}
        <div className="space-y-3">
          <div className={`flex items-center ${ui.gap.sm}`}>
            <input
              type="checkbox"
              id="removeNullValues"
              checked={removeNullValues}
              onChange={(e) => setRemoveNullValues(e.target.checked)}
              className={`${ui.input.base} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
            />
            <label htmlFor="removeNullValues" className={`${ui.text.label} text-gray-700 dark:text-gray-300`}>
              Remove properties with null values
            </label>
          </div>

          <div className={`flex items-center ${ui.gap.sm}`}>
            <input
              type="checkbox"
              id="removeEmptyStrings"
              checked={removeEmptyStrings}
              onChange={(e) => setRemoveEmptyStrings(e.target.checked)}
              className={`${ui.input.base} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
            />
            <label
              htmlFor="removeEmptyStrings"
              className={`${ui.text.label} text-gray-700 dark:text-gray-300`}
            >
              Remove properties with empty strings
            </label>
          </div>

          <div className={`flex items-center ${ui.gap.sm}`}>
            <input
              type="checkbox"
              id="removeEmptyArrays"
              checked={removeEmptyArrays}
              onChange={(e) => setRemoveEmptyArrays(e.target.checked)}
              className={`${ui.input.base} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
            />
            <label htmlFor="removeEmptyArrays" className={`${ui.text.label} text-gray-700 dark:text-gray-300`}>
              Remove properties with empty arrays
            </label>
          </div>

          <div className={`flex items-center ${ui.gap.sm}`}>
            <input
              type="checkbox"
              id="removeEmptyObjects"
              checked={removeEmptyObjects}
              onChange={(e) => setRemoveEmptyObjects(e.target.checked)}
              className={`${ui.input.base} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
            />
            <label
              htmlFor="removeEmptyObjects"
              className={`${ui.text.label} text-gray-700 dark:text-gray-300`}
            >
              Remove properties with empty objects
            </label>
          </div>
        </div>

        {/* Invalid Properties */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Invalid Properties to Remove (Optional)
          </label>
          <input
            type="text"
            value={invalidProperties}
            onChange={(e) => setInvalidProperties(e.target.value)}
            placeholder="tempId, debugField, testProperty"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            Comma-separated list of property names to remove
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeleteInvalidDataParameters;
